import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { AnalyticsPanel } from "../components/AnalyticsPanel";
import { api } from "../lib/api";
import {
  Loader2, LogOut, Check, X, MessageCircle, ScanLine, BellRing,
  Calendar, Phone, IdCard, Car, FileText, Eye, ChevronLeft, UserCheck, UserX,
  Search, Camera, RotateCcw, Download, Wrench, Mail, CheckCircle2, Clock,
  Heart, Instagram,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "all", label: "الكل" },
  { key: "pending", label: "قيد المراجعة" },
  { key: "approved", label: "مُعتمد" },
  { key: "rejected", label: "مرفوض" },
];

export default function AdminDashboard() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [attStats, setAttStats] = useState({ approved_total: 0, attended: 0, not_attended: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [selected, setSelected] = useState(null);
  const [me, setMe] = useState(null);
  const [search, setSearch] = useState("");
  const [plateOpen, setPlateOpen] = useState(false);
  const navigate = useNavigate();

  const openDetails = useCallback(async (regSummary) => {
    // Show summary immediately, then fetch full details (with photos) in background
    setSelected(regSummary);
    try {
      const { data } = await api.get(`/registrations/${regSummary.id}`);
      setSelected((curr) => (curr && curr.id === data.id ? data : curr));
    } catch (e) {
      console.error("Failed to load details:", e);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // For attendance tabs, fetch all approved and filter client-side
      const baseStatus = (tab === "attended" || tab === "not_attended") ? "approved" : tab;
      const params = baseStatus === "all" ? {} : { status_filter: baseStatus };
      const [list, st, aSt] = await Promise.all([
        api.get("/registrations", { params }),
        api.get("/registrations/stats"),
        api.get("/admin/attendance-stats"),
      ]);
      let filtered = list.data;
      if (tab === "attended") filtered = filtered.filter((r) => r.attended);
      else if (tab === "not_attended") filtered = filtered.filter((r) => !r.attended);
      setItems(filtered);
      setStats(st.data);
      setAttStats(aSt.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    if (!localStorage.getItem("rb100_admin_token")) {
      navigate("/admin/login");
      return;
    }
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {});
    load();
  }, [load, navigate]);

  // Client-side filter by search query
  const filteredItems = items.filter((r) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [
      r.name,
      r.plate_number,
      r.car_make,
      r.car_model,
      r.car_year,
      r.mobile,
      r.email,
      r.reference_code,
    ]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));
  });

  const logout = () => {
    localStorage.removeItem("rb100_admin_token");
    navigate("/admin/login");
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/registrations/${id}/status`, { status });
      toast.success(status === "approved" ? "تم اعتماد المشاركة" : "تم رفض المشاركة");
      load();
      if (selected?.id === id) {
        const { data } = await api.get(`/registrations/${id}`);
        setSelected(data);
      }
    } catch {
      toast.error("تعذر تحديث الحالة");
    }
  };

  const sendNotification = async (id, channels) => {
    try {
      const { data } = await api.post(`/registrations/${id}/notify`, { channels });
      if (channels.includes("whatsapp") && data.whatsapp_link) {
        window.open(data.whatsapp_link, "_blank");
      }
      load();
    } catch {
      toast.error("تعذر الإرسال");
    }
  };

  const sendReminder = async (id) => {
    try {
      const { data } = await api.post(`/registrations/${id}/remind`);
      if (data.whatsapp_link) {
        window.open(data.whatsapp_link, "_blank");
      }
    } catch {
      toast.error("تعذّر إرسال التذكير");
    }
  };

  const revertAttendance = async (refCode) => {
    if (!window.confirm("هل تريد إلغاء تسجيل الحضور لهذه المشاركة؟")) return;
    try {
      await api.post("/check-in/revert", { reference_code: refCode });
      toast.success("تم إلغاء تسجيل الحضور");
      load();
      if (selected?.reference_code === refCode) {
        const { data } = await api.get(`/registrations/${selected.id}`);
        setSelected(data);
      }
    } catch {
      toast.error("تعذّر التحديث");
    }
  };

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen bg-white">
      <Navbar />
      <div className="border-b border-rb-border bg-rb-surface">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-rb-ink">
              إدارة المشاركات
            </h1>
            <p className="text-xs tracking-widest text-rb-textMuted uppercase mt-1">
              CONTROL ROOM
            </p>
          </div>
          <div className="flex items-center gap-3">
            {me && (
              <div className="hidden sm:block text-left bg-white border border-rb-border px-4 py-2" data-testid="admin-greeting">
                <div className="text-[10px] tracking-widest text-rb-textMuted uppercase">مرحباً</div>
                <div className="text-sm font-bold text-rb-ink">{me.name}</div>
              </div>
            )}
            <Link
              to="/admin/scanner"
              data-testid="goto-scanner-btn"
              className="flex items-center gap-2 bg-rb-red hover:bg-rb-redHover text-white text-sm px-4 py-2 transition-colors font-bold"
            >
              <ScanLine className="w-4 h-4" />
              <span className="hidden sm:inline">ماسح البوابة</span>
            </Link>
            <Link
              to="/admin/export"
              data-testid="goto-export-btn"
              className="flex items-center gap-2 border border-rb-ink hover:bg-rb-ink hover:text-white text-rb-ink text-sm px-4 py-2 transition-colors font-bold bg-white"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">تصدير PDF</span>
            </Link>
            <Link
              to="/admin/messages"
              data-testid="goto-messages-btn"
              className="flex items-center gap-2 border border-rb-ink hover:bg-rb-ink hover:text-white text-rb-ink text-sm px-4 py-2 transition-colors font-bold bg-white"
            >
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">الرسائل</span>
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 border border-rb-border hover:border-rb-red hover:text-rb-red text-rb-ink text-sm px-4 py-2 transition-colors bg-white"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">خروج</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <AnalyticsPanel />

        <div className="flex items-center gap-3 mb-5">
          <span className="h-[2px] w-8 bg-rb-red" />
          <h2 className="font-heading text-lg font-bold text-rb-ink">المشاركات</h2>
        </div>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <StatCard label="إجمالي" value={stats.total} testid="stat-total" />
          <StatCard label="قيد المراجعة" value={stats.pending} accent="warning" testid="stat-pending" />
          <StatCard label="مُعتمد" value={stats.approved} accent="success" testid="stat-approved" />
          <StatCard label="مرفوض" value={stats.rejected} accent="error" testid="stat-rejected" />
        </div>

        {/* Attendance */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
          <StatCard label="من المعتمدين" value={attStats.approved_total} icon={IdCard} testid="att-total" />
          <StatCard label="حضر" value={attStats.attended} icon={UserCheck} accent="success" testid="att-attended" />
          <StatCard label="لم يحضر" value={attStats.not_attended} icon={UserX} accent="warning" testid="att-not-attended" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 border-b border-rb-border overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-5 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                tab === t.key
                  ? "text-rb-ink border-rb-red"
                  : "text-rb-textMuted border-transparent hover:text-rb-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Search bar */}
        <div className="flex gap-2 mb-6">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rb-textMuted pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="dash-search-input"
              placeholder="ابحث بالاسم، رقم اللوحة، الماركة، الجوال، رقم المرجع..."
              className="w-full bg-white border border-rb-border focus:border-rb-red focus:outline-none focus:ring-2 focus:ring-rb-red/20 px-4 py-3 pr-10 text-rb-ink text-sm transition-colors placeholder:text-rb-textMuted/70"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                data-testid="dash-search-clear"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-rb-textMuted hover:text-rb-ink"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            onClick={() => setPlateOpen(true)}
            data-testid="open-plate-camera-btn"
            className="bg-rb-ink hover:bg-black text-white text-sm px-4 py-3 transition-colors flex items-center gap-2"
            title="تصوير اللوحة للبحث"
          >
            <Camera className="w-4 h-4" />
            <span className="hidden sm:inline">تصوير لوحة</span>
          </button>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-7 h-7 animate-spin text-rb-red" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-24 text-rb-textMuted">
            لا توجد طلبات في هذه الفئة
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-3">
            {filteredItems.map((r) => (
              <RegistrationCard
                key={r.id}
                reg={r}
                onView={() => openDetails(r)}
                onApprove={() => updateStatus(r.id, "approved")}
                onReject={() => updateStatus(r.id, "rejected")}
                onRemind={() => sendReminder(r.id)}
              />
            ))}
            {filteredItems.length === 0 && search && (
              <div className="col-span-full text-center py-12 text-rb-textMuted text-sm">
                لا توجد نتائج للبحث: <span className="font-mono">{search}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {plateOpen && (
        <PlateCameraModal
          onClose={() => setPlateOpen(false)}
          onResult={(plate) => {
            setPlateOpen(false);
            setSearch(plate);
            setTab("all");
            toast.success(`تم استخراج اللوحة: ${plate}`);
          }}
        />
      )}

      {selected && (
        <DetailDrawer
          reg={selected}
          onClose={() => setSelected(null)}
          onApprove={() => updateStatus(selected.id, "approved")}
          onReject={() => updateStatus(selected.id, "rejected")}
          onNotify={(ch) => sendNotification(selected.id, ch)}
          onRemind={() => sendReminder(selected.id)}
          onRevertAttendance={() => revertAttendance(selected.reference_code)}
        />
      )}
    </div>
  );
}

const StatCard = ({ label, value, accent, testid, icon: Icon }) => {
  const colors = {
    success: "text-green-600",
    warning: "text-amber-600",
    error: "text-red-600",
  };
  return (
    <div data-testid={testid} className="bg-white border border-rb-border p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className={`w-4 h-4 ${accent ? colors[accent] : "text-rb-textMuted"}`} />}
        <div className="text-[10px] tracking-[0.25em] text-rb-textMuted uppercase">{label}</div>
      </div>
      <div className={`font-heading text-3xl font-extrabold ${accent ? colors[accent] : "text-rb-ink"}`}>
        {value}
      </div>
    </div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    pending: { label: "قيد المراجعة", cls: "bg-amber-100 text-amber-700 border-amber-300" },
    approved: { label: "مُعتمد", cls: "bg-green-100 text-green-700 border-green-300" },
    rejected: { label: "مرفوض", cls: "bg-red-100 text-red-700 border-red-300" },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`text-[10px] tracking-widest uppercase px-2 py-1 border ${s.cls}`}>
      {s.label}
    </span>
  );
};

const TYPE_META = {
  car_owner: { label: "مالك سيارة", icon: Car, cls: "bg-zinc-100 text-zinc-700 border-zinc-300" },
  photographer: { label: "مصوّر", icon: Camera, cls: "bg-amber-50 text-amber-800 border-amber-300" },
  fan: { label: "هاوي", icon: Heart, cls: "bg-rose-50 text-rose-700 border-rose-300" },
};

const TypePill = ({ type }) => {
  const meta = TYPE_META[type] || TYPE_META.car_owner;
  const Icon = meta.icon;
  return (
    <span className={`text-[10px] tracking-widest px-2 py-1 border flex items-center gap-1 ${meta.cls}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const RegistrationCard = ({ reg, onView, onApprove, onReject, onRemind }) => {
  const mtype = reg.membership_type || "car_owner";
  const isCarOwner = mtype === "car_owner";
  const isPhotographer = mtype === "photographer";
  return (
  <div data-testid={`reg-card-${reg.id}`} className="bg-white border border-rb-border hover:border-rb-red/60 transition-colors p-5 flex gap-4 shadow-sm">
    <div className="w-24 h-24 bg-rb-surface border border-rb-border overflow-hidden shrink-0 relative">
      {reg.photos?.[0]?.data ? (
        <img src={reg.photos[0].data} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-rb-textMuted gap-1">
          {(TYPE_META[mtype]?.icon || Car) && (() => {
            const Ic = TYPE_META[mtype]?.icon || Car;
            return <Ic className="w-7 h-7" />;
          })()}
          {reg.photo_count > 0 && (
            <span className="text-[10px] font-bold">{reg.photo_count} صور</span>
          )}
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <h3 className="font-bold text-base truncate text-rb-ink">{reg.name}</h3>
        <div className="flex gap-1.5 items-center flex-wrap">
          <TypePill type={mtype} />
          <StatusPill status={reg.status} />
          {reg.status === "approved" && <AttendancePill attended={reg.attended} />}
        </div>
      </div>
      <div className="text-xs text-rb-textMuted space-y-1 mb-3">
        {isCarOwner && (
          <div className="flex items-center gap-2">
            <IdCard className="w-3.5 h-3.5" />
            <span className="font-mono">{reg.plate_number}</span>
            {reg.car_make && <span>· {reg.car_make} {reg.car_model}</span>}
          </div>
        )}
        {isPhotographer && (reg.instagram_handle || reg.snapchat_handle) && (
          <div className="flex items-center gap-2 flex-wrap" dir="ltr">
            {reg.instagram_handle && (
              <span className="flex items-center gap-1">
                <Instagram className="w-3.5 h-3.5" />
                @{reg.instagram_handle}
              </span>
            )}
            {reg.snapchat_handle && (
              <span className="flex items-center gap-1">
                <Camera className="w-3.5 h-3.5" />
                @{reg.snapchat_handle}
              </span>
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          <Phone className="w-3.5 h-3.5" />
          <span dir="ltr">{reg.mobile}</span>
        </div>
        {isCarOwner && (
          reg.car_ready === false ? (
            <div className="flex items-center gap-2 text-amber-700 font-bold pt-1">
              <Clock className="w-3.5 h-3.5" />
              <span>
                غير جاهزة
                {reg.expected_ready_date && ` · جاهزة بتاريخ ${reg.expected_ready_date}`}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-green-700 font-bold pt-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>السيارة جاهزة للفعاليات</span>
            </div>
          )
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={onView}
          data-testid={`view-btn-${reg.id}`}
          className="text-xs flex items-center gap-1.5 border border-rb-border hover:border-rb-ink text-rb-ink px-3 py-1.5 transition-colors bg-white"
        >
          <Eye className="w-3.5 h-3.5" />
          عرض
        </button>
        {reg.status !== "approved" && (
          <button
            onClick={onApprove}
            data-testid={`approve-btn-${reg.id}`}
            className="text-xs flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 transition-colors"
          >
            <Check className="w-3.5 h-3.5" />
            اعتماد
          </button>
        )}
        {reg.status !== "rejected" && (
          <button
            onClick={onReject}
            data-testid={`reject-btn-${reg.id}`}
            className="text-xs flex items-center gap-1.5 border border-rb-red text-rb-red hover:bg-rb-red hover:text-white px-3 py-1.5 transition-colors bg-white"
          >
            <X className="w-3.5 h-3.5" />
            رفض
          </button>
        )}
        {reg.status === "approved" && !reg.attended && (
          <button
            onClick={onRemind}
            data-testid={`remind-btn-${reg.id}`}
            className="text-xs flex items-center gap-1.5 border border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white px-3 py-1.5 transition-colors bg-white"
          >
            <BellRing className="w-3.5 h-3.5" />
            تذكير بالحضور
          </button>
        )}
      </div>
    </div>
  </div>
  );
};

const AttendancePill = ({ attended }) => (
  <span
    className={`text-[10px] tracking-widest uppercase px-2 py-1 border flex items-center gap-1 ${
      attended
        ? "bg-blue-50 text-blue-700 border-blue-300"
        : "bg-zinc-50 text-zinc-500 border-zinc-300"
    }`}
  >
    {attended ? <UserCheck className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
    {attended ? "حضر" : "لم يحضر"}
  </span>
);

const DetailDrawer = ({ reg, onClose, onApprove, onReject, onNotify }) => {
  const mtype = reg.membership_type || "car_owner";
  const isCarOwner = mtype === "car_owner";
  const isPhotographer = mtype === "photographer";
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="detail-drawer">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white border-r border-rb-border overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-rb-border px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-heading font-bold text-lg text-rb-ink">{reg.name}</h2>
            <div className="text-xs text-rb-textMuted font-mono">{reg.reference_code}</div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <TypePill type={mtype} />
            <StatusPill status={reg.status} />
            <button onClick={onClose} data-testid="drawer-close" className="text-rb-textMuted hover:text-rb-ink">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <Section icon={<IdCard className="w-4 h-4" />} title="بيانات العضو">
            <Info label="نوع العضوية" value={TYPE_META[mtype]?.label || "—"} />
            <Info label="الاسم" value={reg.name} />
            <Info label="الجوال" value={reg.mobile} dir="ltr" />
            <Info label="البريد" value={reg.email} dir="ltr" />
            {isCarOwner && <Info label="لوحة السيارة" value={reg.plate_number || "—"} />}
          </Section>

          {isCarOwner && (
            <Section icon={<Car className="w-4 h-4" />} title="معلومات السيارة">
              <Info label="الماركة" value={reg.car_make || "—"} />
              <Info label="الموديل" value={reg.car_model || "—"} />
              <Info label="السنة" value={reg.car_year || "—"} />
              <Info
                label="جاهزية للفعاليات"
                value={
                  reg.car_ready === false
                    ? (reg.expected_ready_date
                        ? `غير جاهزة · متوقع: ${reg.expected_ready_date}`
                        : "غير جاهزة حالياً")
                    : "جاهزة ✓"
                }
              />
            </Section>
          )}

          {isPhotographer && (
            <Section icon={<Camera className="w-4 h-4" />} title="حسابات التصوير">
              <Info label="إنستقرام" value={reg.instagram_handle ? `@${reg.instagram_handle}` : "—"} dir="ltr" />
              <Info label="سناب شات" value={reg.snapchat_handle ? `@${reg.snapchat_handle}` : "—"} dir="ltr" />
            </Section>
          )}

          {isCarOwner && reg.photos?.length > 0 && (
            <Section icon={<Eye className="w-4 h-4" />} title="الصور">
              <div className="grid grid-cols-3 gap-2 col-span-2">
                {reg.photos.map((p, i) => (
                  <a key={i} href={p.data} target="_blank" rel="noopener noreferrer" className="aspect-square bg-rb-surface border border-rb-border overflow-hidden block">
                    <img src={p.data} alt="" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </Section>
          )}

          {reg.notes && (
            <Section icon={<FileText className="w-4 h-4" />} title="ملاحظات">
              <div className="col-span-2 text-sm text-rb-ink leading-loose whitespace-pre-line">
                {reg.notes}
              </div>
            </Section>
          )}

          <Section icon={<Calendar className="w-4 h-4" />} title="التواريخ">
            <Info label="تاريخ التسجيل" value={new Date(reg.created_at).toLocaleString("ar")} />
            {reg.approved_at && <Info label="تاريخ الاعتماد" value={new Date(reg.approved_at).toLocaleString("ar")} />}
          </Section>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-rb-border p-4 grid grid-cols-2 gap-2">
          {reg.status === "approved" ? (
            <>
              <button
                onClick={() => onNotify(["whatsapp"])}
                data-testid="drawer-send-whatsapp"
                className="col-span-2 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm px-4 py-3 transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                إرسال البطاقة عبر واتساب
              </button>
              <Link
                to={`/badge/${reg.reference_code}`}
                target="_blank"
                data-testid="drawer-view-badge"
                className="col-span-2 flex items-center justify-center gap-2 border border-rb-border hover:border-rb-ink text-rb-ink text-sm px-4 py-3 transition-colors"
              >
                عرض البطاقة
                <ChevronLeft className="w-4 h-4" />
              </Link>
            </>
          ) : (
            <>
              <button
                onClick={onApprove}
                data-testid="drawer-approve"
                className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm px-4 py-3 transition-colors"
              >
                <Check className="w-4 h-4" />
                اعتماد
              </button>
              <button
                onClick={onReject}
                data-testid="drawer-reject"
                className="flex items-center justify-center gap-2 border border-rb-red text-rb-red hover:bg-rb-red hover:text-white text-sm px-4 py-3 transition-colors bg-white"
              >
                <X className="w-4 h-4" />
                رفض
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Section = ({ icon, title, children }) => (
  <div className="bg-rb-surface border border-rb-border p-5">
    <h3 className="flex items-center gap-2 text-xs tracking-widest text-rb-textMuted uppercase mb-4">
      <span className="text-rb-red">{icon}</span>
      {title}
    </h3>
    <div className="grid grid-cols-2 gap-4">{children}</div>
  </div>
);

const Info = ({ label, value, dir }) => (
  <div>
    <div className="text-[10px] tracking-widest text-rb-textMuted uppercase mb-1">{label}</div>
    <div className="text-sm text-rb-ink" dir={dir}>{value}</div>
  </div>
);

// ---- Plate Camera Modal ----
const PlateCameraModal = ({ onClose, onResult }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [phase, setPhase] = useState("camera"); // camera | preview | sending | error
  const [snapshot, setSnapshot] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const [confidence, setConfidence] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (!active) { s.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          await videoRef.current.play();
        }
      } catch (e) {
        setErrMsg("تعذّر فتح الكاميرا. تأكّد من السماح بالوصول.");
        setPhase("error");
      }
    })();
    return () => {
      active = false;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const capture = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    const w = v.videoWidth || 1280;
    const h = v.videoHeight || 720;
    // Scale down to ~1024 max
    const scale = Math.min(1, 1024 / Math.max(w, h));
    c.width = Math.round(w * scale);
    c.height = Math.round(h * scale);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    const dataUrl = c.toDataURL("image/jpeg", 0.85);
    setSnapshot(dataUrl);
    setPhase("preview");
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  };

  const send = async () => {
    setPhase("sending");
    try {
      const { data } = await api.post("/admin/recognize-plate", { image: snapshot });
      if (!data.plate) {
        setErrMsg("لم يتم اكتشاف لوحة في الصورة. حاول مرة أخرى.");
        setPhase("error");
        return;
      }
      setConfidence(data.confidence);
      onResult(data.plate);
    } catch (e) {
      const msg = e?.response?.data?.detail || "تعذّر التحليل";
      setErrMsg(typeof msg === "string" ? msg : "خطأ");
      setPhase("error");
    }
  };

  const retake = async () => {
    setSnapshot(null);
    setErrMsg("");
    setPhase("camera");
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        await videoRef.current.play();
      }
    } catch {
      setErrMsg("تعذّر فتح الكاميرا.");
      setPhase("error");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="plate-camera-modal">
      <div className="bg-white w-full max-w-2xl border border-rb-border overflow-hidden">
        <div className="border-b border-rb-border px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="font-heading font-bold text-lg text-rb-ink">تصوير لوحة السيارة</h3>
            <p className="text-xs text-rb-textMuted">سيتم استخراج رقم اللوحة تلقائياً ثم البحث عنها</p>
          </div>
          <button onClick={onClose} data-testid="plate-modal-close" className="text-rb-textMuted hover:text-rb-ink">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-black aspect-video relative overflow-hidden">
          {phase === "camera" && (
            <>
              <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="w-[70%] h-[28%] border-2 border-rb-red relative">
                  <div className="absolute -top-1 -right-1 w-5 h-5 border-t-4 border-r-4 border-white" />
                  <div className="absolute -top-1 -left-1 w-5 h-5 border-t-4 border-l-4 border-white" />
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 border-b-4 border-r-4 border-white" />
                  <div className="absolute -bottom-1 -left-1 w-5 h-5 border-b-4 border-l-4 border-white" />
                </div>
              </div>
              <div className="absolute top-3 inset-x-0 text-center text-white text-xs">
                وجّه الكاميرا نحو اللوحة بحيث تكون داخل الإطار
              </div>
            </>
          )}
          {phase === "preview" && snapshot && (
            <img src={snapshot} alt="" className="w-full h-full object-contain bg-black" />
          )}
          {phase === "sending" && snapshot && (
            <>
              <img src={snapshot} alt="" className="w-full h-full object-contain bg-black opacity-60" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                <Loader2 className="w-10 h-10 animate-spin mb-3" />
                <p className="text-sm">جاري قراءة اللوحة...</p>
              </div>
            </>
          )}
          {phase === "error" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-6">
              <X className="w-10 h-10 text-rb-red mb-3" />
              <p className="text-sm mb-1">{errMsg}</p>
            </div>
          )}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        <div className="p-4 flex gap-2 flex-wrap">
          {phase === "camera" && (
            <button
              onClick={capture}
              data-testid="plate-capture-btn"
              className="flex-1 bg-rb-red hover:bg-rb-redHover text-white font-bold px-6 py-3 transition-colors flex items-center justify-center gap-2"
            >
              <Camera className="w-4 h-4" />
              التقاط الصورة
            </button>
          )}
          {phase === "preview" && (
            <>
              <button
                onClick={retake}
                data-testid="plate-retake-btn"
                className="flex items-center justify-center gap-2 border border-rb-border hover:border-rb-ink text-rb-ink text-sm px-4 py-3 transition-colors bg-white"
              >
                <RotateCcw className="w-4 h-4" />
                إعادة
              </button>
              <button
                onClick={send}
                data-testid="plate-send-btn"
                className="flex-1 bg-rb-red hover:bg-rb-redHover text-white font-bold px-6 py-3 transition-colors flex items-center justify-center gap-2"
              >
                <Search className="w-4 h-4" />
                قراءة اللوحة والبحث
              </button>
            </>
          )}
          {phase === "sending" && (
            <div className="flex-1 text-center text-sm text-rb-textMuted py-3">يرجى الانتظار...</div>
          )}
          {phase === "error" && (
            <button
              onClick={retake}
              data-testid="plate-retry-btn"
              className="flex-1 bg-rb-red hover:bg-rb-redHover text-white font-bold px-6 py-3 transition-colors flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              إعادة المحاولة
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
